// PartsBase Automation — owner/admin control surface for shadow sourcing.
//
// Shows Off, Shadow and Manual send. The AUTONOMOUS modes (limited_auto, auto)
// are still not offered, because the worker that would act on them does not
// exist and a control that names a mode the system cannot honour teaches an
// operator something false about their own setup.
//
// Manual send is different: it is the mode that says "a person may press Send",
// and that path is built. The server enforces the same split independently —
// send-gate.ts takes the KIND of send being authorised and manual_send answers
// no to the autonomous one — so this list is a convenience, not the boundary.
//
// Two things this page is careful about:
//
//   * Every usage figure is labelled ESTIMATED. PartsBase exposes no credit,
//     quota or rate data at all -- verified live, the search response body's
//     entire top level is {items} -- so presenting these as real credits would
//     be a fabrication, not a rounding error.
//   * Evaluated candidates and intended dispatches are shown as separate
//     numbers with separate labels. Conflating them was a real defect: a
//     campaign that evaluated 12 listings and selected 3 read as "12
//     dispatches", which is 12 outbound messages to real companies that were
//     never going to be sent.
//
// Enforcement lives in the worker and the database. Turning this page off does
// not turn off the caps.

import { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pause, Play, ShieldCheck, TriangleAlert, Activity, History } from 'lucide-react';
import { AppShell } from '@/components/app-shell';
import { api } from '@/lib/api';

interface UsagePeriod {
  period: 'minute' | 'hour' | 'day' | 'month';
  periodKey: string;
  used: number;
  reserved: number;
  limit: number | null;
  estimated: boolean;
  percentUsed: number | null;
  warning: boolean;
  hardStopped: boolean;
}

interface Settings {
  paused: boolean;
  pausedReason: string | null;
  maxSearchesPerMinute: number;
  maxSearchesPerHour: number;
  maxSearchesPerDay: number;
  monthlyEstimatedUnitBudget: number | null;
  searchResultLimit: number;
  cacheTtlHours: number;
  warningThresholdPct: number;
  parserConfidenceThreshold: number | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

interface StatusResponse {
  mode: string;
  availableModes: string[];
  settings: Settings;
  usage: UsagePeriod[];
  decisions: Array<{ status: string; n: number }>;
  reasonCodes: Array<{ code: string; n: number }>;
  funnel: {
    candidates_evaluated: number;
    candidates_eligible: number;
    dispatches_intended: number;
    dispatches_sent: number;
  } | null;
  exclusions: Array<{ reason: string; n: number }>;
  queue: Array<{ status: string; n: number; oldest: string | null }>;
  skips: Array<{ reason: string; n: number; last_seen: string | null }>;
  configMissing: { count: number; lastSeen: string | null } | null;
}

interface CampaignRow {
  id: string;
  status: string;
  decision: string | null;
  reason_codes: string[];
  revision: number;
  created_at: string;
  searched_at: string | null;
  policy_version: string;
  mode_at_decision: string;
  provenance: string | null;
  parser_confidence: number | null;
  part_number: string;
  line_public_id: string;
  candidates_evaluated: number;
  dispatches_intended: number;
}

interface Projection {
  threshold: number;
  total_lines: number;
  blocked_invalid_part_number: number;
  eligible_human_entered: number;
  eligible_machine: number;
  review_machine: number;
}

const STATUS_TONE: Record<string, string> = {
  shadow_complete: 'pill-success',
  review_required: 'pill-warning',
  blocked: 'pill-danger',
  failed: 'pill-danger',
  pending: 'pill-default',
  evaluating: 'pill-default',
  searching: 'pill-default',
};

/**
 * Mode -> what the operator sees.
 *
 * Was a binary ternary — `m === 'off' ? 'Off' : 'Shadow'` — which silently
 * labelled every non-off mode "Shadow". The moment manual_send was added the
 * page rendered two identical Shadow buttons and no way to tell them apart.
 * A map fails visibly (an unmapped mode shows its raw name) instead of
 * mislabelling.
 */
export const MODE_LABEL: Record<string, string> = {
  off: 'Off',
  shadow: 'Shadow',
  manual_send: 'Manual send',
  limited_auto: 'Limited auto',
  auto: 'Auto',
};

export function PartsBaseAutomationPage() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const tokenGetter = () => getToken();
  const [draft, setDraft] = useState<Partial<Settings>>({});
  const [showProjections, setShowProjections] = useState(false);

  const status = useQuery({
    queryKey: ['pb-automation'],
    queryFn: () => api<StatusResponse>('/api/partsbase-automation', { tokenGetter }),
    staleTime: 15_000,
  });

  const campaigns = useQuery({
    queryKey: ['pb-automation', 'campaigns'],
    queryFn: () =>
      api<{ campaigns: CampaignRow[] }>('/api/partsbase-automation/campaigns?limit=25', {
        tokenGetter,
      }),
    staleTime: 15_000,
  });

  const audit = useQuery({
    queryKey: ['pb-automation', 'audit'],
    queryFn: () =>
      api<{ events: Array<{ id: string; actor_label: string; entity_type: string; before_json: unknown; after_json: unknown; occurred_at: string }> }>(
        '/api/partsbase-automation/audit?limit=25',
        { tokenGetter },
      ),
    staleTime: 30_000,
  });

  const projections = useQuery({
    queryKey: ['pb-automation', 'projections'],
    queryFn: () =>
      api<{ projections: Projection[]; note: string }>(
        '/api/partsbase-automation/threshold-projections',
        { tokenGetter },
      ),
    enabled: showProjections,
    staleTime: 300_000,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['pb-automation'] });

  const setMode = useMutation({
    mutationFn: (mode: string) =>
      api<{ mode: string }>('/api/partsbase-automation/mode', {
        method: 'PATCH',
        tokenGetter,
        body: JSON.stringify({ mode }),
      }),
    onSuccess: invalidate,
  });

  const saveSettings = useMutation({
    mutationFn: (patch: Partial<Settings>) =>
      api<{ settings: Settings }>('/api/partsbase-automation/settings', {
        method: 'PATCH',
        tokenGetter,
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      setDraft({});
      invalidate();
    },
  });

  const s = status.data;
  const cur: Settings | undefined = s ? { ...s.settings, ...draft } : undefined;
  const dirty = Object.keys(draft).length > 0;
  // `|| 'Save failed'` not `??` — api() yields message:'' for an empty-body
  // error, and nullish-coalescing would render nothing at all.
  const saveErr = saveSettings.isError
    ? (saveSettings.error as { message?: string })?.message || 'Save failed'
    : null;
  const modeErr = setMode.isError
    ? (setMode.error as { message?: string })?.message || 'Mode change failed'
    : null;

  const hardStopped = (s?.usage ?? []).some((u) => u.hardStopped);
  const warning = (s?.usage ?? []).some((u) => u.warning);

  const headerActions = (
    <>
      <button
        onClick={() => saveSettings.mutate({ paused: !cur?.paused })}
        disabled={!cur || saveSettings.isPending}
        className="btn-ghost text-xs disabled:opacity-50"
      >
        {cur?.paused ? (
          <>
            <Play className="w-3.5 h-3.5" /> Resume
          </>
        ) : (
          <>
            <Pause className="w-3.5 h-3.5" /> Pause
          </>
        )}
      </button>
      <button
        onClick={() => saveSettings.mutate(draft)}
        disabled={!dirty || saveSettings.isPending}
        className="btn-primary text-xs disabled:opacity-50"
      >
        {saveSettings.isPending ? 'Saving…' : 'Save changes'}
      </button>
    </>
  );

  return (
    <AppShell title="PartsBase Automation" headerActions={headerActions}>
      {status.isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {status.isError && (
        <div className="card p-4 text-sm text-[hsl(var(--danger))]">
          Could not load automation status.
        </div>
      )}

      {s && cur && (
        <div className="flex flex-col gap-4">
          {/* The fault the preflight caught: work silently skipped because a
              service was never configured. Loud, first, and unmissable. */}
          {s.configMissing && (
            <section className="card p-4 border-2 border-[hsl(var(--danger))]">
              <h2 className="text-sm font-semibold flex items-center gap-2 text-[hsl(var(--danger))]">
                <TriangleAlert className="w-4 h-4" /> Automation is configured on, but work is
                being skipped
              </h2>
              <p className="text-xs mt-2">
                <strong>{s.configMissing.count}</strong> event
                {s.configMissing.count === 1 ? ' has' : 's have'} been skipped because the
                background worker is missing its PartsBase configuration. Campaigns are being
                created and the queue looks healthy, but <strong>no searches are running</strong>.
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Fix: set the <code>PARTSBASE_*</code> variables on the <strong>worker</strong>{' '}
                service, not only the web service. Last seen{' '}
                {s.configMissing.lastSeen
                  ? new Date(s.configMissing.lastSeen).toLocaleString()
                  : 'unknown'}
                .
              </p>
            </section>
          )}
          {/* ---------------- Mode + state ---------------- */}
          <section className="card p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" /> Automation mode
                </h2>
                <p className="text-xs text-muted-foreground mt-1">
                  <span className="font-medium">Shadow</span> evaluates and records what it
                  would send, and contacts nobody.{' '}
                  <span className="font-medium">Manual send</span> lets an owner or admin
                  select sellers on an RFQ line and send a real RFQ — nothing is ever sent
                  automatically. Autonomous modes are not built.
                </p>
              </div>
              <div className="flex gap-2">
                {s.availableModes.map((m) => (
                  <button
                    key={m}
                    onClick={() => setMode.mutate(m)}
                    disabled={setMode.isPending}
                    className={m === s.mode ? 'btn-primary text-xs' : 'btn-ghost text-xs'}
                  >
                    {MODE_LABEL[m] ?? m}
                  </button>
                ))}
              </div>
            </div>
            {modeErr && <div className="mt-2 text-xs text-[hsl(var(--danger))]">{modeErr}</div>}
            {cur.paused && (
              <div className="mt-3 pill pill-warning text-[11px]">
                Paused{cur.pausedReason ? ` — ${cur.pausedReason}` : ''}
              </div>
            )}
            {hardStopped && (
              <div className="mt-3 flex items-center gap-2 text-xs text-[hsl(var(--danger))]">
                <TriangleAlert className="w-3.5 h-3.5" />
                Hard stop: a configured cap is fully consumed. No further searches will run
                until the period rolls over.
              </div>
            )}
            {!hardStopped && warning && (
              <div className="mt-3 flex items-center gap-2 text-xs text-[hsl(var(--warning))]">
                <TriangleAlert className="w-3.5 h-3.5" />
                Warning threshold reached ({cur.warningThresholdPct}%). Searches continue
                until 100%.
              </div>
            )}
          </section>

          {/* ---------------- Usage ---------------- */}
          <section className="card p-4">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Activity className="w-4 h-4" /> Estimated usage
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              PartsBase publishes no credit, quota or rate figures, so every number here is an
              estimate of one unit per search — not a reading of your PartsBase balance.
            </p>
            <div className="grid gap-3 mt-3 sm:grid-cols-2 lg:grid-cols-4">
              {s.usage.map((u) => (
                <div key={u.period} className="rounded border border-border p-3">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    {u.period}
                  </div>
                  <div className="text-lg font-semibold mt-1">
                    {u.used + u.reserved}
                    <span className="text-sm text-muted-foreground font-normal">
                      {' '}/ {u.limit ?? '∞'}
                    </span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    {u.percentUsed === null ? 'no cap configured' : `${u.percentUsed}% used`}
                    {u.reserved > 0 && ` · ${u.reserved} in flight`}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1 italic">estimated</div>
                </div>
              ))}
            </div>
          </section>

          {/* ---------------- Candidates vs dispatches ---------------- */}
          {s.funnel && (
            <section className="card p-4">
              <h2 className="text-sm font-semibold">Evaluation funnel</h2>
              <p className="text-xs text-muted-foreground mt-1">
                Evaluated listings and intended recipients are different numbers. Only the
                third counts as an outbound message.
              </p>
              <div className="grid gap-3 mt-3 sm:grid-cols-2 lg:grid-cols-4">
                <Stat label="Listings evaluated" value={s.funnel.candidates_evaluated} />
                <Stat label="Passed the filters" value={s.funnel.candidates_eligible} />
                <Stat
                  label="Would be contacted"
                  value={s.funnel.dispatches_intended}
                  hint="recipients, not listings"
                />
                <Stat
                  label="Actually sent"
                  value={s.funnel.dispatches_sent}
                  hint="always 0 in shadow"
                />
              </div>
              {s.exclusions.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {s.exclusions.map((x) => (
                    <span key={x.reason} className="pill pill-default text-[11px]">
                      {x.reason.replace(/_/g, ' ')}: {x.n}
                    </span>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* ---------------- Settings ---------------- */}
          <section className="card p-4">
            <h2 className="text-sm font-semibold">Limits</h2>
            <div className="grid gap-3 mt-3 sm:grid-cols-2 lg:grid-cols-3">
              <NumField
                label="Searches per minute"
                value={cur.maxSearchesPerMinute}
                onChange={(v) => setDraft((d) => ({ ...d, maxSearchesPerMinute: v }))}
              />
              <NumField
                label="Searches per hour"
                value={cur.maxSearchesPerHour}
                onChange={(v) => setDraft((d) => ({ ...d, maxSearchesPerHour: v }))}
              />
              <NumField
                label="Searches per day"
                value={cur.maxSearchesPerDay}
                onChange={(v) => setDraft((d) => ({ ...d, maxSearchesPerDay: v }))}
              />
              <NullableNumField
                label="Monthly estimated units"
                value={cur.monthlyEstimatedUnitBudget}
                onChange={(v) => setDraft((d) => ({ ...d, monthlyEstimatedUnitBudget: v }))}
              />
              <NumField
                label="Listings per search"
                value={cur.searchResultLimit}
                onChange={(v) => setDraft((d) => ({ ...d, searchResultLimit: v }))}
              />
              <NumField
                label="Warning threshold (%)"
                value={cur.warningThresholdPct}
                onChange={(v) => setDraft((d) => ({ ...d, warningThresholdPct: v }))}
              />
            </div>

            <div className="mt-4 border-t border-border pt-4">
              <h3 className="text-sm font-semibold">Parser confidence threshold</h3>
              <p className="text-xs text-muted-foreground mt-1">
                Applies <strong>only</strong> to part numbers a parser extracted from email
                text. A part number typed into a structured field by a customer or by staff is
                eligible regardless — it has no parser confidence because no parser ran.
                Leave blank to measure without gating.
              </p>
              <div className="flex flex-wrap items-end gap-3 mt-3">
                <NullableNumField
                  label="Threshold (0–1)"
                  value={cur.parserConfidenceThreshold}
                  step={0.05}
                  onChange={(v) => setDraft((d) => ({ ...d, parserConfidenceThreshold: v }))}
                />
                <button
                  onClick={() => setShowProjections((v) => !v)}
                  className="btn-ghost text-xs"
                >
                  {showProjections ? 'Hide' : 'Show'} projected impact
                </button>
              </div>

              {showProjections && (
                <div className="mt-3 overflow-x-auto">
                  {projections.isLoading && (
                    <div className="text-xs text-muted-foreground">Calculating…</div>
                  )}
                  {projections.data && (
                    <>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-muted-foreground">
                            <th className="py-1 pr-3">Threshold</th>
                            <th className="py-1 pr-3">Eligible (human-entered)</th>
                            <th className="py-1 pr-3">Eligible (extracted)</th>
                            <th className="py-1 pr-3">To review</th>
                            <th className="py-1 pr-3">Blocked</th>
                          </tr>
                        </thead>
                        <tbody>
                          {projections.data.projections.map((p) => (
                            <tr
                              key={p.threshold}
                              className={
                                cur.parserConfidenceThreshold === p.threshold
                                  ? 'font-semibold'
                                  : ''
                              }
                            >
                              <td className="py-1 pr-3">{p.threshold.toFixed(2)}</td>
                              <td className="py-1 pr-3">{p.eligible_human_entered}</td>
                              <td className="py-1 pr-3">{p.eligible_machine}</td>
                              <td className="py-1 pr-3">{p.review_machine}</td>
                              <td className="py-1 pr-3">{p.blocked_invalid_part_number}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <p className="text-[11px] text-muted-foreground mt-2">{projections.data.note}</p>
                    </>
                  )}
                </div>
              )}
            </div>

            {saveErr && <div className="mt-3 text-xs text-[hsl(var(--danger))]">{saveErr}</div>}
            {cur.updatedBy && (
              <div className="mt-3 text-[11px] text-muted-foreground">
                Last changed by {cur.updatedBy}
                {cur.updatedAt ? ` on ${new Date(cur.updatedAt).toLocaleString()}` : ''}
              </div>
            )}
          </section>

          {/* ---------------- Recent campaigns ---------------- */}
          <section className="card p-4">
            <h2 className="text-sm font-semibold">Recent shadow decisions</h2>
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="py-1 pr-3">Part</th>
                    <th className="py-1 pr-3">Status</th>
                    <th className="py-1 pr-3">Provenance</th>
                    <th className="py-1 pr-3">Conf.</th>
                    <th className="py-1 pr-3">Evaluated</th>
                    <th className="py-1 pr-3">Would contact</th>
                    <th className="py-1 pr-3">Reasons</th>
                  </tr>
                </thead>
                <tbody>
                  {(campaigns.data?.campaigns ?? []).map((c) => (
                    <tr key={c.id} className="border-t border-border">
                      <td className="py-1.5 pr-3 font-mono">{c.part_number}</td>
                      <td className="py-1.5 pr-3">
                        <span className={`pill ${STATUS_TONE[c.status] ?? 'pill-default'} text-[10px]`}>
                          {c.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="py-1.5 pr-3 text-muted-foreground">
                        {c.provenance === 'machine_extracted' ? 'extracted' : 'human'}
                      </td>
                      <td className="py-1.5 pr-3 text-muted-foreground">
                        {c.parser_confidence === null ? '—' : Number(c.parser_confidence).toFixed(2)}
                      </td>
                      <td className="py-1.5 pr-3">{c.candidates_evaluated}</td>
                      <td className="py-1.5 pr-3">{c.dispatches_intended}</td>
                      <td className="py-1.5 pr-3 text-muted-foreground">
                        {(c.reason_codes ?? []).join(', ') || '—'}
                      </td>
                    </tr>
                  ))}
                  {(campaigns.data?.campaigns ?? []).length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-3 text-muted-foreground">
                        No campaigns yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* ---------------- Queue health ---------------- */}
          <section className="card p-4">
            <h2 className="text-sm font-semibold">Queue health</h2>
            <div className="flex flex-wrap gap-2 mt-3">
              {s.queue.length === 0 && <span className="text-xs text-muted-foreground">No events.</span>}
              {s.queue.map((q) => (
                <span
                  key={q.status}
                  className={`pill text-[11px] ${q.status === 'dead' ? 'pill-danger' : 'pill-default'}`}
                >
                  {q.status}: {q.n}
                  {q.oldest && ` · oldest ${new Date(q.oldest).toLocaleDateString()}`}
                </span>
              ))}
            </div>
            {s.skips.length > 0 && (
              <>
                <h3 className="text-xs font-semibold mt-4">Why work was skipped</h3>
                <p className="text-[11px] text-muted-foreground mt-1">
                  &ldquo;Deliberately off&rdquo; and &ldquo;never configured&rdquo; look identical
                  in a status column. They are separated here on purpose.
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {s.skips.map((k) => (
                    <span
                      key={k.reason}
                      className={`pill text-[11px] ${
                        k.reason === 'config_missing' ? 'pill-danger' : 'pill-default'
                      }`}
                    >
                      {k.reason.replace(/_/g, ' ')}: {k.n}
                    </span>
                  ))}
                </div>
              </>
            )}
          </section>

          {/* ---------------- Settings audit ---------------- */}
          <section className="card p-4">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <History className="w-4 h-4" /> Settings history
            </h2>
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="py-1 pr-3">When</th>
                    <th className="py-1 pr-3">Who</th>
                    <th className="py-1 pr-3">What</th>
                    <th className="py-1 pr-3">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {(audit.data?.events ?? []).map((e) => (
                    <tr key={e.id} className="border-t border-border">
                      <td className="py-1.5 pr-3">{new Date(e.occurred_at).toLocaleString()}</td>
                      <td className="py-1.5 pr-3">{e.actor_label}</td>
                      <td className="py-1.5 pr-3 text-muted-foreground">
                        {e.entity_type === 'tenant_sourcing_mode' ? 'mode' : 'limits'}
                      </td>
                      <td className="py-1.5 pr-3 font-mono text-[10px]">
                        {JSON.stringify(e.before_json)} → {JSON.stringify(e.after_json)}
                      </td>
                    </tr>
                  ))}
                  {(audit.data?.events ?? []).length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-3 text-muted-foreground">
                        No changes recorded yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </AppShell>
  );
}

function Stat({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <div className="rounded border border-border p-3">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold mt-1">{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground mt-1 italic">{hint}</div>}
    </div>
  );
}

/** A cap that must always have a value. Clearing the box yields 0, which the
 *  server then rejects with a readable message rather than silently unsetting
 *  a limit. */
function NumField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <Field label={label}>
      <input
        type="number"
        step={1}
        className="w-full px-2 py-1.5 border border-border rounded text-sm bg-background"
        value={value}
        onChange={(e) => {
          const n = Number(e.target.value);
          onChange(Number.isFinite(n) ? n : 0);
        }}
      />
    </Field>
  );
}

/** A setting where "not set" is meaningful -- the monthly budget and the
 *  confidence threshold. Blank means null, which disables that control rather
 *  than setting it to zero. */
function NullableNumField({
  label,
  value,
  onChange,
  step,
}: {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
  step?: number;
}) {
  return (
    <Field label={label}>
      <input
        type="number"
        step={step ?? 1}
        className="w-full px-2 py-1.5 border border-border rounded text-sm bg-background"
        value={value ?? ''}
        placeholder="not set"
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') return onChange(null);
          const n = Number(raw);
          onChange(Number.isFinite(n) ? n : null);
        }}
      />
    </Field>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
